import { useState, useMemo } from 'react';
import * as PopoverPrimitive from '@radix-ui/react-popover';
import { useTranslation } from 'react-i18next';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { IconDefinition } from '@fortawesome/fontawesome-svg-core';
import {
  faLinkedin,
  faFacebook,
  faInstagram,
  faXTwitter,
  faYoutube,
  faTiktok,
  faPinterest,
  faThreads,
  faGoogle,
  faBluesky,
} from '@fortawesome/free-brands-svg-icons';
import { faGlobe } from '@fortawesome/free-solid-svg-icons';
import { cn } from '@/utils/cn';
import { filterTriggerHover } from './filterTriggerStyles';

/**
 * ChannelSelect Component
 *
 * Single-select dropdown for channels with platform icons
 * Shows FontAwesome brand icons for each platform
 * Includes "All Channels" option when value is null
 */

interface PlatformInfo {
  color: string;
  icon: IconDefinition;
  name: string;
}

const PLATFORM_INFO: Record<string, PlatformInfo> = {
  LINKEDIN: { color: '#0A66C2', icon: faLinkedin, name: 'LinkedIn' },
  FACEBOOK: { color: '#1877F2', icon: faFacebook, name: 'Facebook' },
  INSTAGRAM: { color: '#E4405F', icon: faInstagram, name: 'Instagram' },
  TWITTER: { color: '#000000', icon: faXTwitter, name: 'X' },
  YOUTUBE: { color: '#FF0000', icon: faYoutube, name: 'YouTube' },
  TIKTOK: { color: '#000000', icon: faTiktok, name: 'TikTok' },
  PINTEREST: { color: '#E60023', icon: faPinterest, name: 'Pinterest' },
  THREADS: { color: '#000000', icon: faThreads, name: 'Threads' },
  GOOGLE_MY_BUSINESS: { color: '#4285F4', icon: faGoogle, name: 'Google' },
  BLUESKY: { color: '#0085FF', icon: faBluesky, name: 'Bluesky' },
};

const getPlatformInfo = (platform: string): PlatformInfo => {
  return PLATFORM_INFO[platform] || { color: '#6B7280', icon: faGlobe, name: platform };
};

export interface ChannelSelectOption {
  id: number;
  name: string;
  platform: string;
  avatarUrl?: string | null;
}

export interface ChannelSelectProps {
  value: number | null; // null = all channels
  onChange: (id: number | null) => void;
  options: ChannelSelectOption[];
  placeholder?: string;
  disabled?: boolean;
  className?: string;
  size?: 'sm' | 'md';
}

export const ChannelSelect = ({
  value,
  onChange,
  options,
  placeholder = 'All Channels',
  disabled = false,
  className = '',
  size = 'md',
}: ChannelSelectProps) => {
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const [search, setSearch] = useState('');

  const filteredOptions = useMemo(() => {
    if (!search.trim()) return options;

    const searchLower = search.toLowerCase();
    return options.filter(
      (option) =>
        option.name.toLowerCase().includes(searchLower) ||
        option.platform.toLowerCase().includes(searchLower)
    );
  }, [options, search]);

  const selectedOption = options.find((opt) => opt.id === value);

  const handleSelect = (optionId: number | null) => {
    onChange(optionId);
    setIsOpen(false);
  };

  const handleOpenChange = (open: boolean) => {
    setIsOpen(open);
    if (!open) {
      setSearch('');
    }
  };

  return (
    <div className={cn('w-full', className)}>
      <PopoverPrimitive.Root open={isOpen} onOpenChange={handleOpenChange}>
        <PopoverPrimitive.Trigger asChild>
          <button
            type="button"
            disabled={disabled}
            className={cn(
              size === 'sm' ? 'w-full px-3 h-[34px] text-xs lg:h-auto lg:px-4 lg:py-2 lg:text-sm' : 'w-full px-4 py-2 text-sm',
              'border border-gray-200 rounded-lg',
              'text-left',
              'focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500',
              'transition-all duration-200',
              'disabled:bg-gray-50 disabled:cursor-not-allowed',
              'flex items-center justify-between gap-2',
              !disabled && filterTriggerHover
            )}
          >
            <span className="flex items-center gap-2 flex-1 min-w-0">
              {selectedOption ? (
                <>
                  {/* Selected channel avatar */}
                  <div className="relative flex-shrink-0">
                    {selectedOption.avatarUrl ? (
                      <>
                        <img
                          src={selectedOption.avatarUrl}
                          alt=""
                          className="w-6 h-6 rounded-full object-cover"
                        />
                        {/* Platform badge */}
                        <span
                          className="absolute -bottom-0.5 -right-0.5 w-3 h-3 rounded-full flex items-center justify-center border border-white"
                          style={{ backgroundColor: getPlatformInfo(selectedOption.platform).color }}
                        >
                          <FontAwesomeIcon
                            icon={getPlatformInfo(selectedOption.platform).icon}
                            className="text-white text-[6px]"
                          />
                        </span>
                      </>
                    ) : (
                      <span
                        className="w-6 h-6 rounded-full flex items-center justify-center"
                        style={{ backgroundColor: getPlatformInfo(selectedOption.platform).color }}
                      >
                        <FontAwesomeIcon
                          icon={getPlatformInfo(selectedOption.platform).icon}
                          className="text-white text-xs"
                        />
                      </span>
                    )}
                  </div>
                  <span className="truncate text-gray-700">{selectedOption.name}</span>
                </>
              ) : (
                <>
                  {/* All Channels placeholder */}
                  <span
                    className="w-6 h-6 rounded-full flex items-center justify-center bg-gray-200"
                  >
                    <FontAwesomeIcon
                      icon={faGlobe}
                      className="text-gray-500 text-xs"
                    />
                  </span>
                  <span className="text-gray-400">{placeholder}</span>
                </>
              )}
            </span>
            <svg
              width="12"
              height="12"
              viewBox="0 0 12 12"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
              className={cn('transition-transform duration-200 flex-shrink-0', isOpen && 'rotate-180')}
            >
              <path
                d="M2.5 4.5L6 8L9.5 4.5"
                stroke="currentColor"
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </button>
        </PopoverPrimitive.Trigger>

        <PopoverPrimitive.Portal>
          <PopoverPrimitive.Content
            className={cn(
              'bg-white rounded-lg shadow-lg',
              'border border-gray-200',
              'w-[var(--radix-popover-trigger-width)]',
              'max-h-[320px]',
              'flex flex-col',
              'z-popover-top'
            )}
            sideOffset={5}
            align="start"
          >
            {/* Search Input */}
            <div className="p-3 border-b border-gray-100">
              <input
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search channels..."
                className="w-full px-3 py-2 text-sm border border-gray-200 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                autoFocus
              />
            </div>

            {/* Options List */}
            <div className="overflow-y-auto flex-1 p-2">
              {/* All Channels Option */}
              <button
                type="button"
                onClick={() => handleSelect(null)}
                className={cn(
                  'w-full flex items-center gap-3 px-3 py-2',
                  'hover:bg-gray-50 rounded-md cursor-pointer',
                  'transition-colors text-left',
                  value === null && 'bg-primary-50'
                )}
              >
                <span
                  className="w-8 h-8 rounded-full flex items-center justify-center bg-gray-100 flex-shrink-0"
                >
                  <FontAwesomeIcon
                    icon={faGlobe}
                    className="text-gray-500 text-sm"
                  />
                </span>
                <div className="flex-1 min-w-0">
                  <div className="text-sm text-gray-700 font-medium">
                    {placeholder}
                  </div>
                  <div className="text-xs text-gray-500">
                    {t('show_all_channels', { defaultValue: 'Show all channels' })}
                  </div>
                </div>
              </button>

              {/* Channel Options */}
              {filteredOptions.length > 0 ? (
                <div className="space-y-1 mt-1">
                  {filteredOptions.map((option) => {
                    const isSelected = value === option.id;
                    const platformInfo = getPlatformInfo(option.platform);

                    return (
                      <button
                        key={option.id}
                        type="button"
                        onClick={() => handleSelect(option.id)}
                        className={cn(
                          'w-full flex items-center gap-3 px-3 py-2',
                          'hover:bg-gray-50 rounded-md cursor-pointer',
                          'transition-colors text-left',
                          isSelected && 'bg-primary-50'
                        )}
                      >
                        {/* Avatar with platform icon badge */}
                        <div className="relative flex-shrink-0">
                          {option.avatarUrl ? (
                            <>
                              <img
                                src={option.avatarUrl}
                                alt=""
                                className="w-8 h-8 rounded-full object-cover"
                              />
                              {/* Platform badge (shown when avatar exists) */}
                              <span
                                className="absolute -bottom-0.5 -right-0.5 w-4 h-4 rounded-full flex items-center justify-center border-2 border-white"
                                style={{ backgroundColor: platformInfo.color }}
                              >
                                <FontAwesomeIcon
                                  icon={platformInfo.icon}
                                  className="text-white text-[7px]"
                                />
                              </span>
                            </>
                          ) : (
                            <span
                              className="w-8 h-8 rounded-full flex items-center justify-center"
                              style={{ backgroundColor: platformInfo.color }}
                            >
                              <FontAwesomeIcon
                                icon={platformInfo.icon}
                                className="text-white text-sm"
                              />
                            </span>
                          )}
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="text-sm text-gray-700 font-medium truncate">
                            {option.name}
                          </div>
                          <div className="text-xs text-gray-500">{platformInfo.name}</div>
                        </div>
                      </button>
                    );
                  })}
                </div>
              ) : (
                <div className="p-4 text-center">
                  <p className="text-sm text-gray-500">
                    {search ? 'No channels found' : 'No channels available'}
                  </p>
                </div>
              )}
            </div>
          </PopoverPrimitive.Content>
        </PopoverPrimitive.Portal>
      </PopoverPrimitive.Root>
    </div>
  );
};
